import React, { useEffect, useState } from "react";
import { fabric } from 'fabric';
import html2canvas from 'html2canvas';
import style from './TableComponent.module.scss';
import { useDispatch, useSelector } from "react-redux";
import { showTableModal, saveDrawing } from '../../../actions/wizard';

const TableModalComponent = (props: any) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState(0);
  const [table, setTable] = useState([]);
  const [columns, setColumns] = useState(0);
  const [rowHeights, setRowHeights] = useState([]);
  const [columnWidths, setColumnWidths] = useState([]);
  const [columnHeadings, setColumnHeadings] = useState([]);
  const [tableTitle, setTableTitle] = useState(props.title);
  const [hideDeleteBtn, setHideDeleteBtn] = useState(false);
  const [editTableDetails, setEditTableDetails] = useState('');
  const fabricInstance = useSelector((state: any) => state.wizard.fabricInstance);
  const showTableCreationModal = useSelector((state: any) => state.wizard.showTableCreationModal);

  // Fetching table row and column details 
  useEffect(() => {
    setTable(showTableCreationModal);
    setRows(showTableCreationModal?.length || 0);
    setColumns(showTableCreationModal?.[0]?.length || 0);
    setRowHeights(props?.tableObjDetails?.tableRowHeight?.length ? props?.tableObjDetails?.tableRowHeight : Array(showTableCreationModal?.length || 0).fill(20));
    setColumnHeadings(props?.tableHeadings.length ? props?.tableHeadings : Array(showTableCreationModal?.[0]?.length || 0).fill(''));
    setColumnWidths(props?.tableObjDetails?.tableColWidth?.length ? props?.tableObjDetails?.tableColWidth : Array(showTableCreationModal?.[0]?.length || 0).fill(120)); // Default width of 100px
  }, [showTableCreationModal])


  // Saving table details in canvas after hiding the input fields
  useEffect(() => {
    if (hideDeleteBtn) {
      saveTable()
    }
  }, [hideDeleteBtn])


  useEffect(() => {
    const selectedTable = fabricInstance.getActiveObject();
    if(selectedTable && selectedTable?.tableCode?.length) {
      setEditTableDetails(selectedTable);
    }
  }, [])

  // Function to update the textarea fields
  const handleInputChange = (e, rowIndex, colIndex) => {
    const newTable = [...table];
    newTable[rowIndex][colIndex] = e.target.value;
    setTable(newTable);
    deleteExitingTable();
  };

  // Function to update the heading details
  const handleHeadingChange = (e, colIndex) => {
    const newHeadings = [...columnHeadings];
    newHeadings[colIndex] = e.target.value;
    setColumnHeadings(newHeadings);
    deleteExitingTable();
  };

  // Function to add a new rows
  const addRow = (rowIndex) => {
    const newTable = [
      ...table.slice(0, rowIndex + 1),
      Array(columnHeadings?.length).fill(""),
      ...table.slice(rowIndex + 1),
    ];
    setTable(newTable);
    setRowHeights([
      ...rowHeights.slice(0, rowIndex + 1),
      20, // Default height of 30px
      ...rowHeights.slice(rowIndex + 1),
    ]);
    deleteExitingTable();
  };

  // Function to add a new columns
  const addColumn = (colIndex) => {
    const newTable = table.map((row) => [
      ...row.slice(0, colIndex + 1),
      "",
      ...row.slice(colIndex + 1),
    ]);
    setTable(newTable);
    setColumnWidths([
      ...columnWidths.slice(0, colIndex + 1),
      100, // Default width of 100px
      ...columnWidths.slice(colIndex + 1),
    ]);
    setColumnHeadings([
      ...columnHeadings.slice(0, colIndex + 1),
      "Header",
      ...columnHeadings.slice(colIndex + 1),
    ]);
    deleteExitingTable();
  };

  // Function to delete the row
  const deleteRow = (rowIndex) => {
    const newTable = table.filter((_, index) => index !== rowIndex);
    setTable(newTable);
    setRowHeights(rowHeights.filter((_, index) => index !== rowIndex));
    deleteExitingTable();
  };

  // Function to delete the column
  const deleteColumn = (colIndex) => {
    const newTable = table.map(row => row.filter((_, index) => index !== colIndex));
    setTable(newTable);
    setColumnWidths(columnWidths.filter((_, index) => index !== colIndex));
    setColumnHeadings(columnHeadings.filter((_, index) => index !== colIndex));
    deleteExitingTable();
  };

  // Function to handle the column and row textarea size update.
  const handleTextareaResize = (e, rowIndex, colIndex) => {
    const textarea = e.target;
    const newWidth = textarea.offsetWidth;
    const newHeight = textarea.offsetHeight;

    if (columnWidths[colIndex] !== newWidth) {
      const newColumnWidths = [...columnWidths];
      newColumnWidths[colIndex] = newWidth;
      setColumnWidths(newColumnWidths);
    }

    if (rowHeights[rowIndex] !== newHeight) {
      const newRowHeights = [...rowHeights];
      newRowHeights[rowIndex] = newHeight;
      setRowHeights(newRowHeights);
    }    
    deleteExitingTable();
  };

  // Setting table every row and column width and height.
  useEffect(() => {
    const handleResize = () => {
      setRowHeights(rowHeights.map((_, rowIndex) => {
        const textarea = document.getElementById(`textarea-${rowIndex}-0`);
        return textarea ? textarea.offsetHeight : 20;
      }));
      setColumnWidths(columnWidths.map((_, colIndex) => {
        const textarea = document.getElementById(`textarea-0-${colIndex}`);
        return textarea ? textarea.offsetWidth : 100;
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [columnWidths, rowHeights]);

  // Update the others column and rows textarea size based upon the current field update.
  useEffect(() => {
    table.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const textarea = document.getElementById(`textarea-${rowIndex}-${colIndex}`);
        if (textarea) {
          textarea.style.height = `${rowHeights[rowIndex]}px`;
          textarea.style.width = `${columnWidths[colIndex]}px`;
        }
      });
    });
  }, [columnWidths, rowHeights]);

  // Function to save the table image in canvas
  const saveTable = () => {
    const canvas = fabricInstance
    const htmlTable = document.getElementById('custom_table_element');
    html2canvas(htmlTable).then((canvasEl) => {
      const imgData = canvasEl.toDataURL('image/png');
      fabric.Image.fromURL(imgData, (img) => {
        const selectTableDetails: any = editTableDetails;
        const tableObj = {
          tableRowHeight: rowHeights,
          tableColWidth: columnWidths
        }
        img.set({ tableCode: table, tableTitle: tableTitle, tableHeading: columnHeadings, tableOtherDetails: tableObj, left: selectTableDetails?.left || 50, top: selectTableDetails?.top || 50 });
        canvas.add(img);
        img.scaleToWidth(selectTableDetails?.width * selectTableDetails?.scaleX || 300);  // Adjust size as needed
        canvas.renderAll();
        dispatch(saveDrawing(''))
      });
    });
    dispatch(showTableModal(''))
  }

  // Function to delete exiting table Image from canvas.
  const deleteExitingTable = () => {
    if(editTableDetails) {
      fabricInstance.remove(editTableDetails);
    }
  }

  return (
    <section className={style.tableModal_component}>
      <div className={style.modal_container}>
        <div className={style.modal_head}>
          <h2>Create a Table</h2>
          <span onClick={() => dispatch(showTableModal(''))}>&#x2715;</span>
        </div>
        <div className={style.modal_body}>
          {table.length > 0 && (
            <div className={style.table_editor}>
              <table id="custom_table_element">
                <thead>
                  <tr style={{ display: (hideDeleteBtn && !tableTitle) ?  'none' : '' }}>
                    <th colSpan={columnHeadings.length} style={{ padding: '5px' }}>
                      <input
                        type="text"
                        value={tableTitle}
                        style={{ fontSize: '14px', border: hideDeleteBtn ? 'none' : '', backgroundColor: hideDeleteBtn ? 'transparent' : '', height: '20px' }}
                        placeholder="Enter title here."
                        onChange={(e) => {setTableTitle(e.target.value); deleteExitingTable()}}
                        className={hideDeleteBtn ? style.field_update : ''}
                      />
                      { !hideDeleteBtn && !tableTitle ? <span className={style.tooltip_text}>Please add tittle here.</span> : '' }
                    </th>
                  </tr>
                  <tr>
                    {columnHeadings.map((header, colIndex) => (
                      <th key={colIndex} style={{ width: columnWidths[colIndex] }}>
                        <input
                          value={header}
                          placeholder={`Heading ${colIndex + 1}`}
                          onChange={(e) => handleHeadingChange(e, colIndex)}
                          className={hideDeleteBtn ? style.field_update : ''}
                          style={{ width: '100%', height: '20px'}}
                        />
                        {
                          (colIndex === (columnHeadings?.length - 1) && !hideDeleteBtn && !header) ? <span className={style.tooltip_text}>Please add column heading here.</span> : '' 
                        }
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ height: rowHeights[rowIndex] }}>
                      {row.map((cell, colIndex) => (
                        <td key={colIndex} style={{ width: columnWidths[colIndex] }}>
                          <textarea
                            id={`textarea-${rowIndex}-${colIndex}`}
                            value={cell}
                            className={hideDeleteBtn ? style.field_update : ''}
                            onChange={(e) => handleInputChange(e, rowIndex, colIndex)}
                            onMouseUp={(e) => handleTextareaResize(e, rowIndex, colIndex)}
                            style={{ width: '100%', height: '100%', color: '#6C6C6C' }}
                          />
                        </td>
                      ))}
                      <div className={`${style.border_none} ${style.row_action_btn}`} style={{ display: hideDeleteBtn ? 'none' : '' }}>
                        <i className="fa fa-trash-o" aria-hidden="true" onClick={() => deleteRow(rowIndex)} />
                        <i className="fa fa-plus-circle" aria-hidden="true" onClick={() => addRow(rowIndex)}/>
                      </div>
                    </tr>
                  ))}
                  <tr className={`${style.border_none}`} style={{ display: hideDeleteBtn ? 'none' : '' }}>
                    {columnHeadings.map((_, colIndex) => (
                      <td key={colIndex}>
                        <i className="fa fa-trash-o" aria-hidden="true" onClick={() => deleteColumn(colIndex)} />
                        <i className="fa fa-plus-circle" aria-hidden="true" onClick={() => addColumn(colIndex)}/>
                      </td>
                    ))}
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className={style.modal_footer}>
          <p className={style.create_table_btn} onClick={() => setHideDeleteBtn(true)}>Submit</p>
        </div>
      </div>
    </section>
  )
}

export default TableModalComponent;